import { TextField } from "@mui/material";

import useAssetTableJson from "../../../../../json/useAssetTableJson";
import {
  GridAlignment,
  GridColDef,
  GridColumnVisibilityModel,
  GridValidRowModel,
} from "@mui/x-data-grid-pro";
import { TableHeaderProps } from "../../../../../utils/types";
import { useContext, useEffect, useMemo, useState } from "react";
import { ColumnVisibilityFor } from "../../../../../utils/Enum.Datagrid";
import {
  StyledDatagrid,
  useDataGridStyles,
} from "../../../../../styles/DatagridStyles";
import { CustomDrawer } from "../../../../../styles/CustomDrawerStyles";
import FillProgressDetails from "./FillProgressDetails";
import useServerDateandTime from "../../../../../customHooks/useServerDateandTime";
import {
  toInputStandardDate,
  toStandardDate,
} from "../../../../../utils/UtilFunctions";
import {
  GetProjectDailyReports,
  GetProjectsHeaders,
} from "../../masters/projects/queries/queries";
import { useLazyQuery } from "@apollo/client";
import {
  AtsProjectDailyReportsOrderField,
  GetProjectDailyReportsResult,
  GetProjectDailyReportsVar,
  GetProjectsHeadersResult,
  GetProjectsHeadersVar,
} from "../../masters/projects/queries/types";
import useUrlParams from "../../../../../customHooks/useUrlParams";
import { OrderDirection } from "../../../../../utils/commonQueryTypes";
import { formDates } from "../../purchase/material/ApprovedList";
import {
  HEADER_TEXT_ALIGN,
  MARKS_TEXT_ALIGN,
  SLNO_TEXT_ALIGN,
  TABLE_ROW_HEIGHT,
} from "../../../../../styles/Variables";
import {
  DataTypes,
  Operation,
  TableHeaders,
  empType,
} from "../../../../../utils/Enum.types";
import Edit from "../../../../../images/EditProfile.svg";
import FillRemarks from "./FillRemarks";
import { AppContext } from "../../../../../context/context";

interface extendedTableHeaders extends TableHeaderProps {
  idx: number;
}

const WorkProgressTable = () => {
  const { WorkInProgress } = useAssetTableJson();
  const { headerStyles } = useDataGridStyles();
  const [fillWorkProgress, setFillWorkProgress] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [reportsID, setReportsID] = useState<number>(0);
  const [dates, setDates] = useState<formDates>({
    startDate: "",
    endDate: "",
  });
  const { state } = useContext(AppContext);

  const { serverDate } = useServerDateandTime();

  const { customer_id, projectID } = useUrlParams();

  const [headers, setHeaders] = useState<extendedTableHeaders[]>([]);

  const [GetReports, { data }] = useLazyQuery<
    GetProjectDailyReportsResult,
    GetProjectDailyReportsVar
  >(GetProjectDailyReports);

  const [GetHeaders, { data: headersData }] = useLazyQuery<
    GetProjectsHeadersResult,
    GetProjectsHeadersVar
  >(GetProjectsHeaders);

  const dynamicHeaders: TableHeaderProps[] = WorkInProgress.ProjectStatus.map(
    (header) => ({
      headerName: header.headerName,
      field: header.field,
      headerAlign: header.headerAlign as GridAlignment,
      align: header.align as GridAlignment,
      flex: header.flex,
      hideable: header.hideable,
      cellClassName: header.cellClassName,
      renderCell: (params) => {
        if (header.field === "actions") {
          const [day, month, year] = params.row.date.split("-").map(Number);
          const date1 = new Date(year, month - 1, day);
          const date2 = new Date(serverDate);

          const areSame = date1.toDateString() === date2.toDateString();
          if (state.empSubType === empType.PMU && params.row.pmu_remarks) {
            return (
              <img
                src={Edit}
                alt=""
                onClick={() => {
                  setReportsID(params.row.projectDetailId);
                  setEditModal(true);
                }}
              />
            );
          } else if (state.empSubType === empType.VENDOR_MANAGER && areSame) {
            return (
              <img
                src={Edit}
                alt=""
                onClick={() => {
                  setReportsID(params.row.projectDetailId);
                  setFillWorkProgress(true);
                }}
              />
            );
          }
        }
        return params.value;
      },
    })
  );

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({
      id: true,
      date: true,
      quantity: true,
      foundation_completed: true,
      it_hardware_installation: true,
      pending_work: true,
      completed_go_live: true,
      remarks: false,
      status: true,
      pmu_remarks: true,
    });

  useEffect(() => {
    const savedModel = localStorage.getItem(
      ColumnVisibilityFor.WORK_IN_PROGRESS
    );
    if (savedModel) {
      setColumnVisibilityModel(JSON.parse(savedModel));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(
      ColumnVisibilityFor.WORK_IN_PROGRESS,
      JSON.stringify(columnVisibilityModel)
    );
  }, [columnVisibilityModel]);

  useEffect(() => {
    if (serverDate) {
      setDates({
        startDate: "",
        endDate: serverDate.toString(),
      });
    }
  }, [serverDate]);

  useEffect(() => {
    if (customer_id && projectID) {
      GetReports({
        variables: {
          after: null,
          customerID: customer_id,
          projectID,
          first: 12,
          orderBy: {
            direction: OrderDirection.ASC,
            field: AtsProjectDailyReportsOrderField.REPORT_DATE,
          },
        },
      });
    }
  }, [customer_id, projectID, GetReports]);

  const columns = useMemo(() => {
    if (headersData && headersData.GetProjectsHeaders) {
      return [
        ...dynamicHeaders.slice(0, 2),
        ...headersData.GetProjectsHeaders.map((header, index) => ({
          headerName: header.header,
          field: `${index + 1}`,
          headerAlign: HEADER_TEXT_ALIGN,
          align: SLNO_TEXT_ALIGN,
          hideable: true,
          cellClassName: "",
          idx: header.idx,
        })),
        ...dynamicHeaders.slice(2),
      ];
    } else {
      return dynamicHeaders;
    }
  }, [headersData, dynamicHeaders]);

  const rows: GridValidRowModel[] = useMemo(() => {
    const modifiedData = data ? data.GetProjectDailyReports.edges : [];

    const finalModification = modifiedData.map((res, index) => {
      const recordData = headersData
        ? headersData.GetProjectsHeaders.reduce((acc, curr, i) => {
            if (curr.dataType === DataTypes.DATE) {
              acc[curr.idx] = (res.node as any)[`param${curr.idx}`]
                ? toStandardDate((res.node as any)[`param${curr.idx}`])
                : "";
            } else {
              acc[i + 1] = (res.node as any)[`param${i + 1}`].toString();
            }
            return acc;
          }, {} as Record<string, string | number | boolean>)
        : {};

      return {
        id: index + 1,
        date: toStandardDate(res.node.reportDate),
        ...recordData,
        pmu_remarks: res.node.pmuRemarks,
        projectDetailId: res.node.id,
      };
    });

    return data ? [...finalModification] : [];
  }, [data, headersData]);

  useEffect(() => {
    if (customer_id && projectID) {
      GetHeaders({
        variables: {
          customerID: customer_id,
          projectID,
        },
      });
    }
  }, [customer_id, projectID, GetHeaders, headersData]);

  return (
    <>
      {/* <div className="wp-project-details__block--row row g-0"> */}
        {/* <div className="col-2">
          <ChooseMonth
            currentDate={currentDate}
            setCurrentDate={setCurrentDate}
          />
        </div> */}
        {/* <div className="col"></div> */}

        {/* <div className="col-2 flex-end">
          <TextField
            type="date"
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
            label="Date"
            className="work-progress__row--text-field"
            onChange={(e) => {
              setDates({
                ...dates,
                startDate: e.target.value,
              });
            }}
            value={dates.startDate ? toInputStandardDate(dates.startDate) : ""}
          />
          <TextField
            type="date"
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
            label="Date"
            onChange={(e) => {
              setDates({
                ...dates,
                endDate: e.target.value,
              });
            }}
            className="work-progress__row--text-field"
            value={serverDate ? toInputStandardDate(serverDate) : ""}
          />
        </div> */}
      {/* </div> */}
  
        <div className="wp-project-details__block--table">
          <StyledDatagrid
            columns={columns}
            rows={rows}
            disableRowSelectionOnClick
            hideFooter
            columnVisibilityModel={columnVisibilityModel}
            onColumnVisibilityModelChange={(newModel) =>
              setColumnVisibilityModel(newModel)
            }
          rowHeight={TABLE_ROW_HEIGHT}
          
          />
        </div>
    

      <CustomDrawer
        anchor="right"
        open={editModal}
        onClose={() => setEditModal(!editModal)}
      >
        <FillRemarks
          setDrawer={setEditModal}
          operation={Operation.UPDATE}
          AtsProjectDailyReportsID={reportsID}
        />
      </CustomDrawer>

      <CustomDrawer
        anchor="right"
        open={fillWorkProgress}
        onClose={() => setFillWorkProgress(!fillWorkProgress)}
      >
        <FillProgressDetails
          setDrawer={setFillWorkProgress}
          operation={Operation.UPDATE}
          reportsID={reportsID}
        />
      </CustomDrawer>
    </>
  );
};

export default WorkProgressTable;
